"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/utilities/core/cn";

export interface HorizontalScrollProps {
    children: React.ReactNode;
    /** Additional class for the scroll container */
    className?: string;
    /** Gap between items (Tailwind class like "gap-2", "gap-4") */
    gap?: string;
    /** Show navigation arrows on desktop */
    showArrows?: boolean;
    /** Arrow button size */
    arrowSize?: "sm" | "md" | "lg";
}

/**
 * HorizontalScroll - A touch-friendly horizontal scroll container with hidden scrollbar.
 *
 * Features:
 * - Completely hides the scrollbar on all browsers
 * - Native touch scrolling on mobile devices
 * - Optional navigation arrows on desktop
 * - Smooth scroll animation when using arrows
 * - Fade edges to indicate more content
 */
export const HorizontalScroll: React.FC<HorizontalScrollProps> = ({
    children,
    className,
    gap = "gap-3",
    showArrows = true,
    arrowSize = "md",
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // Check scroll position and update arrow visibility
    const checkScrollPosition = useCallback(() => {
        const container = scrollRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }, []);

    // Set up scroll listener and initial check
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        checkScrollPosition();
        container.addEventListener("scroll", checkScrollPosition, { passive: true });
        window.addEventListener("resize", checkScrollPosition);

        return () => {
            container.removeEventListener("scroll", checkScrollPosition);
            window.removeEventListener("resize", checkScrollPosition);
        };
    }, [checkScrollPosition]);

    // Scroll by a percentage of the container width
    const scroll = useCallback((direction: "left" | "right") => {
        const container = scrollRef.current;
        if (!container) return;

        const scrollAmount = container.clientWidth * 0.8;
        container.scrollBy({
            left: direction === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth",
        });
    }, []);

    const arrowSizeClasses = {
        sm: "w-7 h-7",
        md: "w-9 h-9",
        lg: "w-11 h-11",
    };

    const iconSizeClasses = {
        sm: "w-4 h-4",
        md: "w-5 h-5",
        lg: "w-6 h-6",
    };

    return (
        <div className="relative group">
            {/* Left Arrow */}
            {showArrows && canScrollLeft && (
                <button
                    onClick={() => scroll("left")}
                    className={cn(
                        "absolute left-0 top-1/2 -translate-y-1/2 z-10",
                        "bg-background/90 backdrop-blur-sm border border-border rounded-full",
                        "shadow-lg hover:bg-background hover:scale-105",
                        "transition-all duration-200",
                        "hidden md:flex items-center justify-center",
                        "opacity-0 group-hover:opacity-100",
                        "-translate-x-1/2 cursor-pointer",
                        arrowSizeClasses[arrowSize]
                    )}
                    aria-label="Scroll left"
                >
                    <ChevronLeft className={cn("text-foreground", iconSizeClasses[arrowSize])} />
                </button>
            )}

            {/* Scroll Container */}
            <div
                ref={scrollRef}
                className={cn(
                    "flex overflow-x-auto",
                    // Hide scrollbar on all browsers
                    "scrollbar-none",
                    "[&::-webkit-scrollbar]:hidden",
                    "[-ms-overflow-style:none]",
                    "[scrollbar-width:none]",
                    // Touch scrolling
                    "touch-pan-x",
                    gap,
                    className
                )}
            >
                {children}
            </div>

            {/* Right Arrow */}
            {showArrows && canScrollRight && (
                <button
                    onClick={() => scroll("right")}
                    className={cn(
                        "absolute right-0 top-1/2 -translate-y-1/2 z-10",
                        "bg-background/90 backdrop-blur-sm border border-border rounded-full",
                        "shadow-lg hover:bg-background hover:scale-105",
                        "transition-all duration-200",
                        "hidden md:flex items-center justify-center",
                        "opacity-0 group-hover:opacity-100",
                        "translate-x-1/2 cursor-pointer",
                        arrowSizeClasses[arrowSize]
                    )}
                    aria-label="Scroll right"
                >
                    <ChevronRight className={cn("text-foreground", iconSizeClasses[arrowSize])} />
                </button>
            )}
        </div>
    );
};
